<template>
  <Head title="Profile" />
  <div
    class="
      max-w-3xl
      px-4
      mx-auto
      mt-8
      sm:px-6
      md:flex md:items-center md:justify-between md:space-x-5
      lg:max-w-7xl lg:px-8
    "
  >
    <div class="flex items-center space-x-5">
      <div
        class="
          w-[90px]
          h-[90px]
          rounded-full
          border-4 border-gray-200
          overflow-hidden
        "
      >
        <img :src="profileImage" :alt="auth.name" class="object-contain" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ auth.name }}
        </h1>
        <p class="text-sm font-medium text-gray-500 dark:text-gray-400">
          Joined on
          <time :datetime="auth.created_at">{{ userJoinedAt }}</time>
        </p>
      </div>
    </div>
    <div
      class="
        flex flex-col-reverse
        mt-6
        space-y-4 space-y-reverse
        justify-stretch
        sm:flex-row-reverse
        sm:justify-end
        sm:space-x-reverse
        sm:space-y-0
        sm:space-x-3
        md:mt-0 md:flex-row md:space-x-3
      "
    >
      <Link
        :href="route('profile.settings')"
        class="
          inline-flex
          items-center
          justify-center
          px-4
          py-2
          text-sm
          font-medium
          text-gray-700
          bg-white
          border border-gray-300
          dark:border-gray-500
          rounded-md
          shadow-sm
          hover:bg-gray-50
          focus:outline-none
          focus:ring-2
          focus:ring-offset-2
          focus:ring-offset-gray-100
          focus:ring-blue-500
          dark:bg-gray-900 dark:text-white dark:hover:bg-gray-700
        "
      >
        Edit Profile
      </Link>
    </div>
  </div>

  <div
    v-show="!isProfileComplete"
    class="mx-auto mt-8 sm:px-6 max-w-3xl lg:max-w-7xl"
  >
    <div class="flex items-center gap-4 bg-blue-600 p-4">
      <div class="flex-shrink-0 px-4">
        <ExclamationIcon class="h-12 w-12 text-white" aria-hidden="true" />
      </div>
      <div class="flex border-l border-white/50">
        <div class="ml-3 space-y-4">
          <h3 class="font-medium text-white tracking-wider uppercase">
            Profile incomplete
          </h3>
          <!-- <hr class="opacity-50" /> -->
          <div class="text-sm text-white">
            <p>
              Your profile is missing some important information. Please
              complete your profile so you can apply for jobs.
            </p>
          </div>
          <div class="mt-4">
            <div class="flex">
              <Link
                :href="route('profile.settings')"
                class="
                  bg-white
                  px-4
                  py-2
                  text-xs
                  rounded
                  tracking-widest
                  text-blue-800
                  uppercase
                "
              >
                Setup Profile
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="
      grid
      max-w-3xl
      grid-cols-1
      gap-6
      mx-auto
      mt-8
      sm:px-6
      lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3
    "
  >
    <div class="space-y-6 lg:col-start-1 lg:col-span-2">
      <!-- Description list-->
      <section aria-labelledby="applicant-information-title">
        <div class="bg-white dark:bg-gray-700 shadow sm:rounded-lg">
          <div class="px-4 py-5 border-gray-200 sm:px-6">
            <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div class="sm:col-span-1">
                <dt
                  class="text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  Email address
                </dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{ auth.email }}
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt
                  class="text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  City
                </dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{ auth.city || `&mdash;` }}
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt
                  class="text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  Phone
                </dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{ auth.phone_number }}
                </dd>
              </div>
              <div class="sm:col-span-1">
                <dt
                  class="text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  Address
                </dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{
                    `${auth.city || `&mdash;`}, ${auth.state || `&mdash;`} ${
                      auth.zipcode || `&mdash;`
                    }`
                  }}
                </dd>
              </div>
              <!-- <div class="sm:col-span-2">
                <dt
                  class="text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  Bio
                </dt>
                <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {{ auth.bio || `&mdash;` }}
                </dd>
              </div> -->
            </dl>
          </div>
          <div>
            <Link
              :href="route('profile.settings')"
              class="
                block
                px-4
                py-4
                text-sm
                font-medium
                text-center text-gray-500
                dark:text-gray-400
                bg-gray-50
                dark:bg-gray-800 dark:hover:bg-opacity-50
                hover:text-gray-700
                dark:hover:text-gray-200
                sm:rounded-b-lg
                transition
              "
              >Edit Profile</Link
            >
          </div>
        </div>
      </section>

      <!-- Saved Jobs -->
      <section aria-labelledby="notes-title">
        <div
          class="
            bg-white
            dark:bg-gray-700
            shadow
            sm:rounded-lg sm:overflow-hidden
          "
        >
          <div class="divide-y divide-gray-200 dark:divide-gray-800">
            <div class="px-4 py-5 sm:px-6">
              <h2
                id="notes-title"
                class="text-lg font-medium text-gray-900 dark:text-gray-100"
              >
                Saved Jobs
              </h2>
            </div>
            <div class="px-4 py-6 sm:px-6">
              <ul v-show="savedJobs.length" role="list" class="space-y-8">
                <li v-for="job in savedJobs" :key="`saved-job-${job.id}`">
                  <div class="flex space-x-3">
                    <div class="flex-shrink-0">
                      <img
                        class="w-10 h-10 rounded-full"
                        :src="job.company.logo"
                        :alt="job.company.name"
                      />
                    </div>
                    <div>
                      <div class="text-sm">
                        <Link
                          :href="route('jobs.show', { job: job.id, slug: job.slug })"
                          class="font-medium text-gray-900"
                        >
                          {{ job.title }}
                        </Link>
                      </div>
                      <div class="mt-1 text-sm text-gray-700">
                        <p>{{ job.description }}</p>
                      </div>
                      <div class="mt-2 space-x-2 text-sm">
                        <span
                          class="
                            bg-gray-200
                            px-2
                            py-0.5
                            rounded-md
                            text-sm
                            font-medium
                          "
                          >{{ job.job_type.name }}</span
                        >
                      </div>
                    </div>
                  </div>
                </li>
              </ul>

              <!-- No Saved Jobs -->
              <div v-if="savedJobs.length === 0" class="text-center">
                <svg
                  class="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    vector-effect="non-scaling-stroke"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  />
                </svg>
                <h3
                  class="
                    mt-2
                    text-sm
                    font-medium
                    text-gray-900
                    dark:text-gray-200
                  "
                >
                  No saved jobs
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get started by browsing and pressing the ♡ button to save that
                  job.
                </p>
                <div class="mt-6">
                  <Link
                    :to="route('jobs')"
                    class="
                      px-4
                      py-2
                      border border-transparent
                      shadow-sm
                      text-sm
                      font-medium
                      rounded-md
                      text-white
                      bg-blue-600
                      hover:bg-blue-700
                      focus:outline-none
                      focus:ring-2
                      focus:ring-offset-2
                      focus:ring-blue-500
                    "
                  >
                    Browse Jobs
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div v-show="false">
            <a
              class="
                block
                px-4
                py-4
                text-sm
                font-medium
                text-center text-gray-500
                dark:text-gray-400
                bg-gray-50
                dark:bg-gray-800 dark:hover:bg-opacity-50
                hover:text-gray-700
                dark:hover:text-gray-200
                sm:rounded-b-lg
                transition
              "
              href="#"
              >View all saved jobs</a
            >
          </div>
        </div>
      </section>
    </div>

    <section
      aria-labelledby="timeline-title"
      class="lg:col-start-3 lg:col-span-1"
    >
      <div
        class="px-4 py-5 bg-white dark:bg-gray-700 shadow sm:rounded-lg sm:px-6"
      >
        <h2
          id="timeline-title"
          class="text-lg font-medium text-gray-900 dark:text-gray-100"
        >
          Recent Applications
        </h2>

        <!-- Activity Feed -->
        <div class="flow-root mt-6">
          <ul
            role="list"
            class="divide-y divide-gray-200 dark:divide-gray-800 -mx-4"
          >
            <li
              class="
                relative
                bg-white
                dark:bg-gray-700
                py-5
                px-4
                hover:bg-gray-50
                focus-within:ring-2
                focus-within:ring-inset
                focus-within:ring-blue-600
                rounded-md
              "
            >
              <div class="flex justify-between space-x-3">
                <div class="min-w-0 flex-1">
                  <a href="#" class="block focus:outline-none">
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    <p
                      class="
                        text-sm
                        font-medium
                        text-gray-900
                        dark:text-gray-100
                        truncate
                      "
                    >
                      Dareen Koski
                    </p>
                  </a>
                </div>
                <time
                  datetime="2021-01-27T16:35"
                  class="
                    flex-shrink-0
                    whitespace-nowrap
                    text-sm text-gray-500
                    dark:text-gray-400
                  "
                  >1d ago</time
                >
              </div>
              <div class="mt-1">
                <p
                  class="line-clamp-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  Doloremque dolorem maiores assumenda dolorem facilis.
                </p>
              </div>
            </li>

            <!-- More messages... -->
          </ul>
        </div>
        <div class="flex flex-col mt-6 justify-stretch">
          <button
            type="button"
            class="
              inline-flex
              items-center
              justify-center
              px-4
              py-2
              text-sm
              font-medium
              text-white
              bg-blue-600
              border border-transparent
              rounded-md
              shadow-sm
              hover:bg-blue-700
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-blue-500
            "
          >
            View all applications
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ExclamationIcon } from "@heroicons/vue/solid";
import { computed } from "vue";
import { DateTime } from "luxon";
import { Link, Head } from "@inertiajs/inertia-vue3";
import md5 from "md5";

export default {
  props: {
    auth: Object,
    savedJobs: Array,
  },

  setup(props) {
    const profileImage = computed(() => {
      if (props.auth.profile_pic) {
        return "/storage/" + props.auth.profile_pic;
      }

      const hash = md5(props.auth.email);

      return `https://www.gravatar.com/avatar/${hash}`;
    });

    const userJoinedAt = computed(() => {
      return DateTime.fromISO(props.auth.created_at).toLocaleString(
        DateTime.DATE_FULL
      );
    });

    const isProfileComplete = computed(() => {
      return (
        ["name", "email", "phone_number", "zipcode", "city", "state"].filter(
          (key) => {
            return !props.auth[key];
          }
        ).length === 0
      );
    });

    return {
      profileImage,
      userJoinedAt,
      route: window.route,
      isProfileComplete,
    };
  },

  components: {
    Link,
    Head,
    ExclamationIcon,
  },
};
</script>
